/**
 * Engine-owned reactive cache for fetched TTS chunks. The chunk fetcher writes
 * results here and `useCurrentAudioChunkQuery` reads them synchronously by
 * key, so chunk-boundary transitions render prefetched chunks without a
 * pending flash, and the fetcher's single-flight stays the only fetch path.
 *
 * Bounded FIFO: sequential playback and the prefetch window only ever touch
 * the most recently inserted ~10 keys, so insertion-order eviction behaves
 * like LRU here without write-on-read bookkeeping. Evicted chunks simply
 * refetch.
 *
 * Entries are `PlaybackChunk`, a discriminated union: readers narrow on
 * `playbackMode` rather than casting per call site.
 */
import { Atom, type AtomRegistry } from 'effect/reactivity';
import type { PlaybackChunk } from '../query/tts-chunks';

export const legacyChunkCacheKey = (params: {
  chapterId: string;
  ttsId: string;
  voice?: string;
}) => `legacy|${params.chapterId}|${params.ttsId}|${params.voice ?? ''}`;

export const docPackChunkCacheKey = (params: {
  audioId: string;
  docPackVersion: number;
  chunkId: string;
  voice?: string;
}) =>
  `doc-pack|${params.audioId}|${params.docPackVersion.toString()}|${params.chunkId}|${params.voice ?? ''}`;

export type AudioChunkCacheState = Readonly<Record<string, PlaybackChunk>>;

const emptyAudioChunkCacheState: AudioChunkCacheState = {};

export const AUDIO_CHUNK_CACHE_CAPACITY = 48;

export const audioChunkCacheAtom = Atom.writable<
  AudioChunkCacheState,
  AudioChunkCacheState
>(
  () => emptyAudioChunkCacheState,
  (ctx, value) => {
    ctx.setSelf(value);
  },
).pipe(Atom.keepAlive);

export const readAudioChunkCacheState = (
  state: AudioChunkCacheState,
  key: string,
): PlaybackChunk | undefined => state[key];

/** The entry the player is playing. The UI reads the playing chunk from this
 * cache, so evicting it mid-chunk would blank the player while the audio plays
 * on. An eviction that hits it marks it `evicted` instead: the UI keeps it, the
 * engine treats it as gone, and it is dropped once the player holds another. */
const heldAudioChunkAtom = Atom.make<
  { readonly key: string; readonly evicted: boolean } | undefined
>(undefined).pipe(Atom.keepAlive);

/** For the engine: an evicted held entry reads as absent, so reloading that
 * chunk fetches it again instead of replaying the evicted entry. */
export const readAudioChunkCache = (
  registry: AtomRegistry.AtomRegistry,
  key: string,
): PlaybackChunk | undefined => {
  const held = registry.get(heldAudioChunkAtom);
  if (held?.key === key && held.evicted) return undefined;
  return readAudioChunkCacheState(registry.get(audioChunkCacheAtom), key);
};

export const insertAudioChunkCacheState = (
  state: AudioChunkCacheState,
  key: string,
  chunk: PlaybackChunk,
): AudioChunkCacheState => {
  const entries = { ...state, [key]: chunk };
  // FIFO eviction rides on string-key insertion order, which the spec
  // guarantees for these non-index keys (an overwrite keeps its original
  // slot rather than moving to the back).
  const keys = Object.keys(entries);
  if (keys.length <= AUDIO_CHUNK_CACHE_CAPACITY) return entries;
  const retained: Record<string, PlaybackChunk> = {};
  for (const retainedKey of keys.slice(
    keys.length - AUDIO_CHUNK_CACHE_CAPACITY,
  )) {
    const retainedChunk = entries[retainedKey];
    if (retainedChunk !== undefined) retained[retainedKey] = retainedChunk;
  }
  return retained;
};

export const writeAudioChunkCache = (
  registry: AtomRegistry.AtomRegistry,
  key: string,
  chunk: PlaybackChunk,
): void => {
  registry.set(
    audioChunkCacheAtom,
    insertAudioChunkCacheState(registry.get(audioChunkCacheAtom), key, chunk),
  );
  const held = registry.get(heldAudioChunkAtom);
  if (held?.key === key && held.evicted)
    registry.set(heldAudioChunkAtom, { key, evicted: false });
};

export const holdAudioChunk = (
  registry: AtomRegistry.AtomRegistry,
  key: string,
  chunk: PlaybackChunk,
): void => {
  const held = registry.get(heldAudioChunkAtom);
  const current = registry.get(audioChunkCacheAtom);
  let state = current;
  if (held?.evicted && held.key !== key)
    state = Object.fromEntries(
      Object.entries(state).filter(([entryKey]) => entryKey !== held.key),
    );
  if (state[key] !== chunk)
    state = insertAudioChunkCacheState(state, key, chunk);
  if (state !== current) registry.set(audioChunkCacheAtom, state);
  registry.set(heldAudioChunkAtom, { key, evicted: false });
};

/** Drop every entry matching the predicate — e.g. chunks whose downloaded
 * content was deleted or rebuilt under the same ttsId, which the versionless
 * `legacy|` keys would otherwise keep serving stale. The held entry stays for
 * the UI until the player moves on (`heldAudioChunkAtom`). */
export const evictAudioChunkCacheWhere = (
  registry: AtomRegistry.AtomRegistry,
  predicate: (chunk: PlaybackChunk) => boolean,
): void => {
  const state = registry.get(audioChunkCacheAtom);
  const held = registry.get(heldAudioChunkAtom);
  const retained: Record<string, PlaybackChunk> = {};
  let evicted = false;
  let heldEvicted = false;
  for (const [key, chunk] of Object.entries(state)) {
    if (!predicate(chunk)) retained[key] = chunk;
    else if (key === held?.key) {
      retained[key] = chunk;
      heldEvicted = true;
    } else evicted = true;
  }
  if (evicted) registry.set(audioChunkCacheAtom, retained);
  if (heldEvicted && held)
    registry.set(heldAudioChunkAtom, { ...held, evicted: true });
};
